import Matter from 'matter-js'

const { Engine, World, Bodies, Body, Events } = Matter

export interface PhysicsConfig {
  width: number
  height: number
  wallThickness: number
  boundaryLineY: number
}

export interface CollisionPair {
  bodyA: Matter.Body
  bodyB: Matter.Body
}

export type CollisionCallback = (pairs: CollisionPair[]) => void

export class Physics {
  engine: Matter.Engine
  world: Matter.World
  config: PhysicsConfig
  walls: Matter.Body[] = []
  private collisionCallbacks: CollisionCallback[] = []

  constructor(config: PhysicsConfig) {
    this.config = config
    this.engine = Engine.create({
      gravity: { x: 0, y: 1, scale: 0.001 }
    })
    this.world = this.engine.world

    this.createWalls()
    this.setupCollisionEvents()
  }

  private createWalls(): void {
    const { width, height, wallThickness } = this.config

    // Left wall
    const leftWall = Bodies.rectangle(
      -wallThickness / 2,
      height / 2,
      wallThickness,
      height,
      { isStatic: true, label: 'wall-left' }
    )

    // Right wall
    const rightWall = Bodies.rectangle(
      width + wallThickness / 2,
      height / 2,
      wallThickness,
      height,
      { isStatic: true, label: 'wall-right' }
    )

    // Bottom wall
    const bottomWall = Bodies.rectangle(
      width / 2,
      height + wallThickness / 2,
      width + wallThickness * 2,
      wallThickness,
      { isStatic: true, label: 'wall-bottom' }
    )

    this.walls = [leftWall, rightWall, bottomWall]
    World.add(this.world, this.walls)
  }

  private setupCollisionEvents(): void {
    Events.on(this.engine, 'collisionStart', (event) => {
      const pairs: CollisionPair[] = event.pairs.map(pair => ({
        bodyA: pair.bodyA,
        bodyB: pair.bodyB
      }))

      this.collisionCallbacks.forEach(callback => callback(pairs))
    })
  }

  onCollision(callback: CollisionCallback): void {
    this.collisionCallbacks.push(callback)
  }

  addBody(body: Matter.Body): void {
    World.add(this.world, body)
  }

  removeBody(body: Matter.Body): void {
    World.remove(this.world, body)
  }

  createCircle(x: number, y: number, radius: number, options?: Partial<Matter.IBodyDefinition>): Matter.Body {
    return Bodies.circle(x, y, radius, {
      restitution: 0.2,
      friction: 0.5,
      frictionAir: 0.01,
      ...options
    })
  }

  setBodyStatic(body: Matter.Body, isStatic: boolean): void {
    Body.setStatic(body, isStatic)
  }

  update(deltaTime: number = 1000 / 60): void {
    Engine.update(this.engine, deltaTime)
  }

  clear(): void {
    // Remove all bodies except walls
    const bodiesToRemove = this.world.bodies.filter(
      body => !body.label.startsWith('wall-')
    )
    World.remove(this.world, bodiesToRemove)
  }
}
